import { Injectable } from '@angular/core';
import type { EditorComponent } from '@tinymce/tinymce-angular';

@Injectable({ providedIn: 'root' })
export class TinyMceHelper {
  // Samma toolbar som i Utlysningsbyggare (utan kommentarsfunktionen) plus verktyg för teckenstorlek.
  public readonly tinyMceConfig: EditorComponent['init'] = {
    language: 'sv_SE',
    plugins: 'lists link code wordcount',
    menubar: 'edit tools',
    toolbar: 'undo redo | fontsize | bold | styles | bullist numlist | link',
    style_formats: [
      { title: 'Stycke', block: 'p' },
      { title: 'Rubrik 1', block: 'h1' },
      { title: 'Rubrik 2', block: 'h2' },
      { title: 'Rubrik 3', block: 'h3' },
    ],
    promotion: false,
    // Kursiv text ska renderas som <em> precis som i CKEditor.
    formats: {
      italic: { inline: 'em' },
    },
    default_link_target: '_blank',
    link_assume_external_targets: true,
    // Speglar appens typografi så att redigeringsytan matchar hur innehållet
    // renderas i formuläret (samma typsnitt, textfärg och rubrikstorlekar).
    content_style: `
      @font-face {
        font-family: 'TradeGothic';
        src: url('/assets/fonts/TradeGothicLT.woff2') format('woff2');
      }
      @font-face {
        font-family: 'TradeGothic';
        src: url('/assets/fonts/TradeGothicLTStd-Bd.woff2') format('woff2');
        font-weight: bold;
      }
      @font-face {
        font-family: 'TradeGothicBoldHeader';
        src: url('/assets/fonts/TradeGothicLT-Bold.woff2') format('woff2');
        font-weight: bold;
      }
      body {
        font-family: TradeGothic, 'Helvetica Nue', Arial, sans-serif;
        font-size: 1rem;
        line-height: 1.5;
        color: #121619;
      }
      h1, h2, h3, h4, h5, h6 {
        font-family: TradeGothic, 'Helvetica Nue', Arial, sans-serif;
        font-weight: 500;
        line-height: 1.2;
        margin-top: 0;
        margin-bottom: 0.5rem;
      }
      h1 { font-size: 2.5rem; }
      h2 { font-size: 2rem; }
      h3 { font-size: 1.75rem; }
      h4 { font-size: 1.5rem; }
      h5 { font-size: 1.25rem; }
      h6 { font-size: 1rem; }
      a {
        color: #b546af;
        text-decoration: underline;
      }
    `,
  };

  // Lägger till target/rel/class på externa länkar, motsvarar CKEditors link-decorator.
  public processExternalLinks(html: string): string {
    if (!html) {
      return html;
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    for (const anchor of container.querySelectorAll('a')) {
      const url = anchor.getAttribute('href') ?? '';

      if (this.isExternalLink(url)) {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
        anchor.classList.add('external-link');
      } else {
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
        anchor.classList.remove('external-link');
      }
    }

    return container.innerHTML;
  }

  private isExternalLink(url: string): boolean {
    return (
      /^(https?:)?\/\//.test(url) &&
      !(
        /portal(\.utv\.|\.sys\.|\.acc\.|\.)vinnova\.se/.test(url) ||
        /etjanster(\.utv\.|\.sys\.|\.acc\.|\.)vinnova\.se/.test(url) ||
        /ansok(\.utv\.|\.sys\.|\.acc\.|\.)vinnova\.se/.test(url) ||
        /projekt(\.utv\.|\.sys\.|\.acc\.|\.)vinnova\.se/.test(url) ||
        /bedomningsportal(\.utv\.|\.sys\.|\.acc\.|\.)vinnova\.se/.test(url)
      )
    );
  }
}
